"use client"

import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { b2cGetProfile, b2cUpdateProfile } from "../api/b2cUserApi"
import { toast } from "react-toastify"
import { ROOT_API_URL } from "../config/apiConfig"

const UserProfile = () => {
  const { companyName } = useParams()
  const navigate = useNavigate()
  console.log("[v0] UserProfile rendering for company:", companyName)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [profile, setProfile] = useState({
    name: "",
    phone: "",
    nationality: "",
    notes: "",
    address: {
      street: "",
      city: "",
      country: "",
    },
    profileImage: "",
  })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem("authToken")
    console.log("[v0] fetchProfile authToken exists:", !!token)
    if (!token) {
      navigate(`/${companyName}/login`)
      return
    }

    try {
      const data = await b2cGetProfile(token)
      if (data.success) {
        setProfile(data.data)
        if (data.data.profileImage) {
          setImagePreview(
            data.data.profileImage.startsWith("http")
              ? data.data.profileImage
              : `${ROOT_API_URL}${data.data.profileImage}`,
          )
        }
      } else {
        toast.error(data.message || "Failed to fetch profile")
      }
    } catch (error) {
      toast.error("An error occurred while fetching profile")
    } finally {
      setLoading(false)
    }
  }, [companyName, navigate])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name.includes("address.")) {
      const addressField = name.split(".")[1]
      setProfile((prev) => ({
        ...prev,
        address: { ...prev.address, [addressField]: value },
      }))
    } else {
      setProfile((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setUpdating(true)
    const token = localStorage.getItem("authToken")

    const formData = new FormData()
    formData.append("name", profile.name)
    formData.append("phone", profile.phone)
    formData.append("nationality", profile.nationality)
    formData.append("notes", profile.notes)
    formData.append("address[street]", profile.address.street)
    formData.append("address[city]", profile.address.city)
    formData.append("address[country]", profile.address.country)
    if (imageFile) {
      formData.append("profileImage", imageFile)
    }

    try {
      const data = await b2cUpdateProfile(token, formData)
      if (data.success) {
        toast.success("Profile updated successfully")
        setProfile(data.data)
        // Update local image preview if needed
        if (data.data.profileImage) {
          setImagePreview(`${ROOT_API_URL}${data.data.profileImage}`)
        }
      } else {
        toast.error(data.message || "Failed to update profile")
      }
    } catch (error) {
      toast.error("An error occurred while updating profile")
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <h2 className="fw-bold mb-4" style={{ color: "#004a87" }}>
                My Profile
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="text-center mb-4">
                  <div className="position-relative d-inline-block">
                    <img
                      src={imagePreview || "/placeholder.svg?height=120&width=120&query=profile-placeholder"}
                      alt="Profile"
                      className="rounded-circle"
                      style={{ width: "120px", height: "120px", objectFit: "cover", border: "4px solid #f8f9fa" }}
                    />
                    <label
                      htmlFor="profileImage"
                      className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle p-2 shadow-sm"
                      style={{
                        cursor: "pointer",
                        width: "35px",
                        height: "35px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-camera-fill"></i>
                      <input
                        type="file"
                        id="profileImage"
                        className="d-none"
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                    </label>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={profile.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      value={profile.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Nationality</label>
                    <input
                      type="text"
                      className="form-control"
                      name="nationality"
                      value={profile.nationality}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email (Read-only)</label>
                    <input type="email" className="form-control bg-light" value={profile.email} readOnly disabled />
                  </div>

                  <hr className="my-4" />
                  <h5 className="mb-3">Address Information</h5>

                  <div className="col-12">
                    <label className="form-label">Street Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address.street"
                      value={profile.address.street}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address.city"
                      value={profile.address.city}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address.country"
                      value={profile.address.country}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-12 mt-4">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      rows="3"
                      value={profile.notes}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <div className="col-12 mt-4">
                    <button type="submit" className="btn btn-primary-navy w-100 py-2" disabled={updating}>
                      {updating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Updating...
                        </>
                      ) : (
                        "Update Profile"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserProfile
